import React from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  ArrowUpRight,
  Plane,
  BedDouble,
  Handshake,
  ShieldCheck,
  Sparkles,
  Globe2,
  Sunrise,
  Sun,
  Sunset,
  Moon,
} from "lucide-react";
import Reveal from "@/components/motion/Reveal";
import MaskedReveal from "@/components/motion/MaskedReveal";
import HexMark from "@/components/motion/HexMark";
import Seo, { buildBreadcrumbs } from "@/components/Seo";

// ─────────────────────────────────────────────────────────────
// Editorial layout: chronological "Guest Journey" — four phases
// arrayed along a vertical accent rail, alternating sides.
// Deliberately distinct from the numbered-card template used by
// EventTech / SiteSelection / VipFilm.
// ─────────────────────────────────────────────────────────────

const JOURNEY = [
  {
    id: "pre-arrival",
    kicker: "T-90",
    label: "Pre-arrival",
    icon: Sunrise,
    tone: "cyan",
    title: "Sourcing the stay — before your guests know where they're going.",
    body: "We negotiate hotel blocks, secure IATAN-rated airfare, and structure attrition safeguards so cost, capacity, and cancellation are locked in months before the first badge is printed.",
    deliverables: [
      "Hotel sourcing, rate negotiation & contracting",
      "IATAN-accredited air contracts",
      "Group block strategy · attrition buy-back",
      "Force-majeure & cancellation architecture",
      "Rooming lists · dietary & accessibility manifests",
    ],
  },
  {
    id: "arrival",
    kicker: "DAY 01",
    label: "Arrival",
    icon: Sun,
    tone: "amber",
    title: "The first hour on the ground — engineered to feel effortless.",
    body: "From airport meet-and-greet to keys-in-hand, we compress the moving parts into one smooth arrival — with our team on the curb, in the lobby, and at the hospitality desk.",
    deliverables: [
      "Airport meet-and-greet (VIP & general)",
      "Ground transportation · sedans, shuttles, motorcoach",
      "Airline & TSA irregular-op escalation",
      "Front-of-house hospitality desk staffing",
      "Room-drop coordination & credential handoff",
    ],
  },
  {
    id: "on-property",
    kicker: "T+02",
    label: "On-Property",
    icon: Sunset,
    tone: "rose",
    title: "The stay — held to the standard of a five-star concierge.",
    body: "Guest issues intercepted before the front desk knows. Suites reassigned, dietary substitutions confirmed, ground moves re-timed — a live housing bureau operating in the background of every program.",
    deliverables: [
      "24/7 housing bureau · run by our team",
      "Room upgrades, releases, and re-blocks",
      "Dietary, medical & accessibility fulfillment",
      "Real-time shuttle re-routing & load monitoring",
      "VIP concierge escalation channel",
    ],
  },
  {
    id: "departure",
    kicker: "T+n",
    label: "Departure",
    icon: Moon,
    tone: "blue",
    title: "Departure — and a receipt that ties out to the penny.",
    body: "Bags out, invoices reconciled, master accounts audited. You get a single post-event report — every room-night, every ride, every folio — reviewed against contract before it hits your finance team.",
    deliverables: [
      "Master account reconciliation & audit",
      "Attrition and F&B minimum reporting",
      "Post-departure survey aggregation",
      "IATAN air-fee & ARC reporting",
      "Vendor final-billing & closeout",
    ],
  },
];

const TONE_MAP = {
  cyan: "from-cyan-500 to-sky-600 shadow-cyan-500/30",
  amber: "from-amber-400 to-orange-500 shadow-amber-500/30",
  rose: "from-rose-400 to-pink-600 shadow-rose-500/30",
  blue: "from-sky-500 to-blue-700 shadow-blue-500/30",
};

const METRICS = [
  { value: "IATAN", label: "Accredited · Since 2022" },
  { value: "20+", label: "Cities · Domestic & Intl." },
  { value: "8,000+", label: "Room-nights managed" },
  { value: "24 / 7", label: "Live housing bureau" },
];

export default function HotelTravel() {
  return (
    <div className="bg-[var(--bg)] text-[var(--text)]">
      <Seo
        title="Hotel & Travel Accommodations — IATAN-Accredited Group Housing"
        description="IATAN-accredited hotel sourcing, group air, ground transportation, and 24/7 housing bureau services. Guest arrival to departure — one accountable team."
        path="/services/hotel-travel"
        image="/home/hero-2.png"
        keywords="hotel block management, group housing bureau, IATAN accredited, group air contracts, event ground transportation, attendee travel management"
        jsonLd={[
          {
            "@context": "https://schema.org",
            "@type": "Service",
            name: "Hotel & Travel Accommodations",
            serviceType: "Group Housing & Travel Management",
            provider: { "@type": "Organization", name: "Magee Enterprises" },
            areaServed: "Global",
            description:
              "IATAN-accredited hotel sourcing, group air contracts, ground transportation, and 24/7 housing bureau for meetings, events, and executive programs.",
          },
          buildBreadcrumbs([
            ["Home", "/"],
            ["Services", "/services"],
            ["Hotel & Travel", "/services/hotel-travel"],
          ]),
        ]}
      />

      {/* ============ HERO ============ */}
      <section className="relative pt-36 md:pt-44 pb-16 md:pb-24 border-b border-[var(--border)] overflow-hidden">
        <div className="absolute inset-0 hex-pattern opacity-45 pointer-events-none" />
        <div className="brand-bloom brand-bloom-cyan w-[540px] h-[540px] -top-32 -right-24 opacity-40" />
        <HexMark
          size={240}
          opacity={0.055}
          className="absolute -left-10 top-24 rotate-[8deg] pointer-events-none hidden md:block"
        />
        <span className="corner-cross" style={{ top: 44, right: 44 }} />

        <div className="relative max-w-[1600px] mx-auto px-6 md:px-10 grid grid-cols-12 gap-6 md:gap-10">
          <div className="col-span-12 lg:col-span-8">
            <div className="flex items-center gap-4 mb-10 flex-wrap">
              <span className="w-10 h-px bg-[var(--text)]/40" />
              <span className="font-mono-brand text-[11px] uppercase tracking-[0.28em] text-[var(--text-muted)]">
                § Hotel &amp; Travel · Ground Ops
              </span>
              <span className="chip !py-1 !px-2.5 !text-[9.5px]">
                <Sparkles size={10} /> IATAN Accredited
              </span>
            </div>
            <MaskedReveal
              lines={[
                "One room block.",
                "One transport plan.",
                "One accountable team.",
              ]}
              className="font-editorial tracking-[-0.03em] leading-[0.94] text-[11vw] md:text-[6.6vw] lg:text-[5.6vw]"
              stagger={0.08}
            />
          </div>
          <div className="col-span-12 lg:col-span-4 flex flex-col justify-end">
            <Reveal delay={0.35}>
              <p className="text-[var(--text)]/75 text-lg leading-relaxed">
                Hotel sourcing, group air, ground transport, and a live housing
                bureau — the invisible layer that makes every arrival feel like
                the front door of a great hotel.
              </p>
              <div className="mt-8 flex flex-wrap gap-3">
                <Link to="/contact" className="btn-primary" data-testid="hotel-hero-cta-quote">
                  Request a proposal <ArrowRight size={14} />
                </Link>
                <Link to="/contact#book" className="btn-dark" data-testid="hotel-hero-cta-book">
                  Book a 15-min call <ArrowUpRight size={14} />
                </Link>
              </div>
            </Reveal>
          </div>
        </div>

        {/* Metric strip */}
        <div className="relative mt-16 md:mt-20 max-w-[1600px] mx-auto px-6 md:px-10">
          <Reveal delay={0.15}>
            <div
              data-testid="hotel-metrics"
              className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-8 pt-8 border-t border-[var(--border)]"
            >
              {METRICS.map((m) => (
                <div key={m.label}>
                  <div className="font-editorial text-3xl md:text-5xl leading-none tracking-tight">
                    {m.value}
                  </div>
                  <div className="mt-3 font-mono-brand text-[10px] uppercase tracking-[0.24em] text-[var(--text-muted)]">
                    {m.label}
                  </div>
                </div>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      {/* ============ HERO IMAGE ============ */}
      <section className="relative -mt-4 md:-mt-8 pb-16 md:pb-24">
        <div className="max-w-[1600px] mx-auto px-4 md:px-10">
          <Reveal>
            <figure className="relative rounded-3xl overflow-hidden border border-[var(--border)] shadow-[0_30px_80px_-40px_rgba(6,60,120,0.45)]">
              <img
                src="https://images.unsplash.com/photo-1566073771259-6a8506099945?auto=format&fit=crop&w=1920&q=80"
                alt="Luxury hotel lobby — group housing bureau by Magee Enterprises"
                loading="lazy"
                className="w-full h-[42vh] md:h-[60vh] object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent pointer-events-none" />
              <figcaption className="absolute bottom-5 left-5 md:bottom-8 md:left-10 max-w-md">
                <span className="font-mono-brand text-[10px] uppercase tracking-[0.24em] text-cyan-300 bg-black/40 backdrop-blur px-2.5 py-1 rounded-full border border-cyan-300/30 inline-block">
                  On-property · run by our team
                </span>
              </figcaption>
            </figure>
          </Reveal>
        </div>
      </section>

      {/* ============ JOURNEY NARRATIVE ============ */}
      <section
        id="journey"
        className="relative py-20 md:py-32 overflow-hidden scroll-mt-28"
      >
        <div className="absolute inset-0 hex-pattern-dense opacity-40 pointer-events-none" />
        <div className="brand-bloom brand-bloom-blue w-[480px] h-[480px] top-1/4 -right-32 opacity-30" />
        <div className="brand-bloom brand-bloom-warm w-[420px] h-[420px] bottom-1/3 -left-24 opacity-30" />
        <HexMark
          size={220}
          opacity={0.06}
          className="absolute -right-8 top-36 -rotate-[16deg] pointer-events-none hidden lg:block"
        />

        <div className="relative max-w-[1200px] mx-auto px-6 md:px-10">
          <Reveal>
            <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-16 md:mb-24">
              <div>
                <span className="chip">
                  <span className="chip-dot" /> The Guest Journey · 4 phases
                </span>
                <h2 className="mt-6 font-editorial text-4xl md:text-6xl lg:text-7xl leading-[0.98] tracking-[-0.03em] max-w-3xl">
                  From gate to lobby —{" "}
                  <span className="bg-gradient-to-r from-cyan-500 to-sky-600 bg-clip-text text-transparent italic font-medium">
                    every hand-off, choreographed.
                  </span>
                </h2>
              </div>
              <p className="max-w-sm text-[var(--text-muted)]">
                A phase-by-phase view of the moments we own on your behalf —
                and what your attendees never have to think about.
              </p>
            </div>
          </Reveal>

          {/* Vertical journey rail */}
          <div className="relative pl-6 sm:pl-10">
            {/* Vertical accent line */}
            <div
              aria-hidden
              className="absolute left-[7px] sm:left-[15px] top-2 bottom-2 w-px bg-gradient-to-b from-cyan-400/50 via-cyan-500/25 to-transparent"
            />

            <div className="space-y-14 md:space-y-20">
              {JOURNEY.map((phase, i) => (
                <Reveal key={phase.id} delay={i * 0.05}>
                  <article
                    data-testid={`journey-phase-${phase.id}`}
                    className="relative grid grid-cols-1 md:grid-cols-5 gap-6 md:gap-10"
                  >
                    {/* Dot on rail */}
                    <span
                      aria-hidden
                      className={`absolute -left-6 sm:-left-10 top-2 w-3 h-3 rounded-full bg-gradient-to-br ${TONE_MAP[phase.tone]} shadow-lg`}
                      style={{ transform: "translateX(50%)" }}
                    />

                    {/* Text column */}
                    <div className="md:col-span-2">
                      <div className="flex items-center gap-3">
                        <div
                          className={`w-10 h-10 rounded-xl bg-gradient-to-br ${TONE_MAP[phase.tone]} text-white flex items-center justify-center shadow-lg`}
                        >
                          <phase.icon size={18} />
                        </div>
                        <span className="font-mono-brand text-[10px] uppercase tracking-[0.24em] text-[var(--text-muted)]">
                          {phase.kicker} · {phase.label}
                        </span>
                      </div>
                      <h3 className="mt-6 font-editorial text-2xl md:text-3xl leading-[1.05] tracking-[-0.02em]">
                        {phase.title}
                      </h3>
                      <p className="mt-4 text-[var(--text-muted)] leading-relaxed text-[15px]">
                        {phase.body}
                      </p>
                    </div>

                    {/* Deliverables column */}
                    <div className="md:col-span-3">
                      <div className="card-white p-6 md:p-8 relative h-full">
                        <span className="corner-cross" style={{ top: 12, right: 12 }} />
                        <div className="font-mono-brand text-[10px] uppercase tracking-[0.24em] text-[var(--text-muted)] mb-4">
                          What we handle
                        </div>
                        <ul className="grid grid-cols-1 gap-2.5">
                          {phase.deliverables.map((d) => (
                            <li
                              key={d}
                              className="flex items-start gap-3 text-[14.5px] leading-snug text-[var(--text)]/85"
                            >
                              <span
                                className={`mt-1.5 shrink-0 inline-block w-1.5 h-1.5 rounded-full bg-gradient-to-br ${TONE_MAP[phase.tone]}`}
                              />
                              <span>{d}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  </article>
                </Reveal>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ============ WHY US ============ */}
      <section className="relative py-20 md:py-28 overflow-hidden bg-[var(--surface)]">
        <div className="absolute inset-0 hex-pattern opacity-40 pointer-events-none" />
        <div className="relative max-w-[1400px] mx-auto px-6 md:px-10">
          <Reveal>
            <div className="mb-14 md:mb-20">
              <span className="chip">
                <ShieldCheck size={12} /> Why teams retain us
              </span>
              <h2 className="mt-6 font-editorial text-4xl md:text-5xl lg:text-6xl leading-[0.98] tracking-[-0.03em] max-w-3xl">
                A hotel program is a contract discipline first —{" "}
                <span className="italic text-cyan-600 font-medium">
                  hospitality second.
                </span>
              </h2>
            </div>
          </Reveal>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6">
            {[
              {
                icon: Handshake,
                title: "Contract discipline",
                body: "Attrition, cancellation, force-majeure, F&B minimums — negotiated line-by-line so surprises don't reach your budget.",
              },
              {
                icon: Globe2,
                title: "IATAN-accredited",
                body: "Air inventory, ARC reporting, and vendor accreditation — the paperwork most agencies quietly outsource.",
              },
              {
                icon: BedDouble,
                title: "A live bureau",
                body: "24/7 room control desk during program dates — issues intercepted before the front desk hears about them.",
              },
            ].map((c) => (
              <Reveal key={c.title}>
                <div className="card-white p-6 md:p-8 h-full relative hover:border-[var(--accent)]/50 transition-all duration-500">
                  <span className="corner-cross" style={{ top: 12, right: 12 }} />
                  <div className="icon-tile mb-6">
                    <c.icon size={20} />
                  </div>
                  <h3 className="font-editorial text-xl md:text-2xl leading-tight">
                    {c.title}
                  </h3>
                  <p className="mt-3 text-[var(--text-muted)] text-[14.5px] leading-relaxed">
                    {c.body}
                  </p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ============ CLOSING CTA (dark) ============ */}
      <section className="relative py-20 md:py-32">
        <div className="max-w-[1400px] mx-auto px-4 md:px-6">
          <div className="card-dark relative overflow-hidden p-8 md:p-16">
            <div className="absolute inset-0 grid-pattern-dark opacity-30" />
            <div className="absolute -top-24 -left-20 w-[380px] h-[380px] rounded-full bg-cyan-500/20 blur-3xl" />
            <div className="absolute -bottom-24 -right-20 w-[420px] h-[420px] rounded-full bg-sky-500/20 blur-3xl" />
            <div className="relative grid grid-cols-12 gap-8 items-end">
              <div className="col-span-12 lg:col-span-8 text-white">
                <span className="chip !bg-white/10 !text-cyan-300 !border-cyan-300/25">
                  <Plane size={12} /> Programs booking Q2 &ndash; Q4 2026
                </span>
                <h3 className="mt-6 font-editorial text-3xl md:text-5xl lg:text-6xl leading-[0.98] tracking-[-0.03em]">
                  Move your program to a team that owns{" "}
                  <span className="bg-gradient-to-r from-cyan-300 via-sky-300 to-cyan-400 bg-clip-text text-transparent italic font-medium">
                    every folio.
                  </span>
                </h3>
                <p className="mt-6 text-white/70 max-w-xl leading-relaxed">
                  A scoped conversation within one business day. Full-service,
                  retained, or embedded — your call.
                </p>
              </div>
              <div className="col-span-12 lg:col-span-4 flex flex-col sm:flex-row lg:justify-end gap-3">
                <Link to="/contact" className="btn-primary justify-center sm:justify-start" data-testid="hotel-footer-cta-quote">
                  Request a proposal <ArrowRight size={14} />
                </Link>
                <Link to="/contact#book" className="btn-dark backdrop-blur justify-center sm:justify-start" data-testid="hotel-footer-cta-book">
                  Book a call <ArrowUpRight size={14} />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
